
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageCircle, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import FollowBackSuggestion from '@/components/messages/FollowBackSuggestion';
import ConnectionsList from '@/components/messages/ConnectionsList';
import ChatArea from '@/components/messages/ChatArea';
import { useFollowBackSuggestions } from '@/hooks/useFollowBackSuggestions';
import { useMessages } from '@/hooks/useMessages';

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { suggestions, dismissSuggestion } = useFollowBackSuggestions();
  const {
    connections,
    selectedUser,
    messages,
    loading,
    handleSelectUser,
    handleSendMessage
  } = useMessages();

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto p-4 flex-1">
        <motion.div 
          className="flex items-center mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)} 
              className="mr-2 hover:bg-primary/10 transition-all duration-300"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
          </motion.div>
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <MessageCircle className="h-6 w-6 text-primary" />
            </motion.div>
            <motion.h1 
              className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Messages
            </motion.h1>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Sparkles className="h-5 w-5 text-primary" />
            </motion.div>
          </div>
        </motion.div>

        {/* Follow Back Suggestions */}
        <AnimatePresence>
          {suggestions.length > 0 && (
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
            >
              <motion.h2 
                className="text-lg font-semibold mb-3 text-foreground"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                Follow Back Suggestions
              </motion.h2>
              {suggestions.slice(0, 3).map((suggestion, index) => (
                <motion.div
                  key={suggestion.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <FollowBackSuggestion
                    userId={suggestion.id}
                    userName={suggestion.full_name}
                    userAvatar={suggestion.avatar_url}
                    onDismiss={() => dismissSuggestion(suggestion.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-200px)]"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ConnectionsList 
              connections={connections}
              selectedUser={selectedUser}
              onSelectUser={handleSelectUser}
              loading={loading}
            />
          </motion.div>
          
          <motion.div
            className="md:col-span-2"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <ChatArea 
              selectedUser={selectedUser}
              messages={messages}
              currentUserId={user?.id}
              onSendMessage={handleSendMessage}
              loading={loading}
            />
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Messages;
